import { computed, Injectable, signal } from '@angular/core';
import { DefinitionApiService } from '../api/definition-api.service';
import { catchError, finalize, forkJoin, Observable, of, tap, switchMap } from 'rxjs';
import { CreateDefinitionValueDto, CreateMachineManufacturerModelDto, DefinitionModelDto, DeleteDefinitionValueDto, DeleteMachineManufacturerModelDto } from '../../models/definitions/definition-model-dto';
import { EnumValue } from '../../models/definitions/definition-key-value-models';
import { DefinitionType } from '../../enums/definition-type';
import { MachineManufacturerModelDto } from '../../models/definitions/machine-manufacturer-model-dto';

@Injectable({
  providedIn: 'root'
})
export class DefinitionService {
  private harvesterHeadsCache = signal<DefinitionModelDto[]>([]);
  private machineComputersCache = signal<DefinitionModelDto[]>([]);
  private machineManufacturerCache = signal<DefinitionModelDto[]>([]);
  private machineManufacturerModelsCache = signal<MachineManufacturerModelDto[]>([]);
  private machinePerformanceTypesCache = signal<EnumValue[]>([]);

  private loadingState = signal<boolean>(false);
  private errorState = signal<string | null>(null);

  // Public accessors
  harvesterHeads = computed(() => this.harvesterHeadsCache());
  machineComputers = computed(() => this.machineComputersCache());
  machineManufacturers = computed(() => this.machineManufacturerCache());
  machineManufacturerModels = computed(() => this.machineManufacturerModelsCache());
  machinePerformanceTypes = computed(() => this.machinePerformanceTypesCache());

  isLoading = computed(() => this.loadingState());
  error = computed(() => this.errorState());

  constructor(private definitionApiService: DefinitionApiService) { }

  loadMachineComputers(): void {
    if (this.loadingState() || (this.machineComputersCache().length > 0 && !this.errorState())) {
      return;
    }

    this.loadingState.set(true);
    this.errorState.set(null);

    this.definitionApiService.getMachineComputers().pipe(
      finalize(() => this.loadingState.set(false))
    ).subscribe({
      next: (machineComputers) => {
        this.machineComputersCache.set(machineComputers);
      },
      error: (err) => {
        this.errorState.set('Failed to load types');
        console.error('Error loading types', err);
      }
    });
  }

  loadHarvesterHeads(): void {
    if (this.loadingState() || (this.harvesterHeadsCache().length > 0 && !this.errorState())) {
      return;
    }

    this.loadingState.set(true);
    this.errorState.set(null);

    this.definitionApiService.getHarvesterHeads().pipe(
      finalize(() => this)
    ).subscribe({
      next: (harvesterHeads) => {
        this.harvesterHeadsCache.set(harvesterHeads);
      },
      error: (err) => {
        this.errorState.set('Failed to load types');
        console.error('Error loading types', err);
      }
    });
  }

  loadMachineManufacturers(): void {
    if (this.loadingState() || (this.machineManufacturerCache().length > 0 && !this.errorState())) {
      return;
    }
    this.loadingState.set(true);
    this.errorState.set(null);

    this.definitionApiService.getMachineManufacturers().pipe(
      finalize(() => this.loadingState.set(false))
    ).subscribe({
      next: (machineManufacturers) => {
        this.machineManufacturerCache.set(machineManufacturers);
      },
      error: (err) => {
        this.errorState.set('Failed to load types');
        console.error('Error loading types', err);
      }
    });
  }

  loadMachineManufacturerModels(manufacturerId: number): void {
    if (!manufacturerId) {
      this.machineManufacturerModelsCache.set([]);
      return;
    }

    this.loadingState.set(true);
    this.errorState.set(null);

    this.definitionApiService.getMachineManufacturerModels(manufacturerId).pipe(
      finalize(() => this.loadingState.set(false))
    ).subscribe({
      next: (machineManufacturerModels) => {
        const sortedMachineManufacturerModels = machineManufacturerModels.sort((a, b) => a.name.localeCompare(b.name));
        this.machineManufacturerModelsCache.set(sortedMachineManufacturerModels);
      },
      error: (err) => {
        this.errorState.set('Failed to load machinemanufacturermodels');
        console.error('Error loading types', err);
      }
    });
  }

  loadAllDefinitions(): void {
    this.loadingState.set(true);
    forkJoin({
      machineManufacturers: this.definitionApiService.getMachineManufacturers().pipe(
        catchError(err => {
          console.error('Error loading machinemanufacturers', err);
          this.setError('Failed to load machinemanufacturers');
          return of([]);
        })
      ),
      harvesterHeads: this.definitionApiService.getHarvesterHeads().pipe(
        catchError(err => {
          console.error('Error loading harvesterheads', err);
          this.setError('Failed to load harvesterheads');
          return of([]);
        })
      ),
      machineComputers: this.definitionApiService.getMachineComputers().pipe(
        catchError(err => {
          console.error('Error loading machine computers', err);
          this.setError('Failed to load machine computers');
          return of([]);
        })
      ),
      machinePerformanceTypes: this.definitionApiService.getMachinePerformanceTypes().pipe(
        catchError(err => {
          console.error('Error loading machine performancetypes', err);
          this.setError('Failed to load machine performancetypes');
          return of([]);
        })
      )
    }).pipe(
      finalize(() => this.loadingState.set(false))
    ).subscribe({
      next: (results) => {
        this.machineManufacturerCache.set(results.machineManufacturers);
        this.harvesterHeadsCache.set(results.harvesterHeads)
        this.machineComputersCache.set(results.machineComputers);
        this.machinePerformanceTypesCache.set(results.machinePerformanceTypes);
      }
    });
  }

  updateDefinitionValue(definitionType: DefinitionType, updateDefinitionModel: DefinitionModelDto) {
    this.definitionApiService.updateDefinitionValue(definitionType, updateDefinitionModel).subscribe();
  }

  updateMachineManufacturerModelValue(updateModelModel: MachineManufacturerModelDto) {
    this.definitionApiService.updateMachineManufacturerModelValue(updateModelModel).subscribe();
  }

  createDefinitionValue(definitionType: DefinitionType, createDefinitionModel: CreateDefinitionValueDto): Observable<DefinitionModelDto> {
    return this.definitionApiService.createDefinitionValue(definitionType, createDefinitionModel).pipe(
      tap(() => this.loadAllDefinitions())
    );
  }

  createMachineManufacturerModel(machineManufacturerId: number, createMachineManufacturerModel: CreateMachineManufacturerModelDto): Observable<MachineManufacturerModelDto> {
    return this.definitionApiService.createMachineManufacturerModel(machineManufacturerId, createMachineManufacturerModel);
  }

  deleteDefinitionValue(definitionType: DefinitionType, deleteDefinitionModel: DeleteDefinitionValueDto) {
    this.loadingState.set(true);
    return this.definitionApiService.deleteDefinitionValue(definitionType, deleteDefinitionModel).pipe(
      tap(() => this.loadAllDefinitions())
    );
  }

  deleteMachineManufacturerModelValue(deleteMachineManufacturerValueModel: DeleteMachineManufacturerModelDto) {
    return this.definitionApiService.deleteMachineManufacturerModelValue(deleteMachineManufacturerValueModel);
  }

  setError(error: string): void {
    this.errorState.set(error);
  }
}
